import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  useTheme,
  useMediaQuery,
  Box,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { Invoice } from '../../types/invoice';
import czechTranslations from '../../translations/czech';

interface InternalNotesDialogProps {
  open: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  onSave: (invoiceId: number, notes: string) => Promise<void>;
}

const InternalNotesDialog: React.FC<InternalNotesDialogProps> = ({
  open,
  onClose,
  invoice,
  onSave
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const translations = czechTranslations.invoices.internalNotes;

  // Update notes when invoice changes
  useEffect(() => {
    if (invoice) {
      setNotes(invoice.internalNotes || '');
    }
  }, [invoice]);

  const handleSave = async () => {
    if (!invoice) return;
    
    setSaving(true);
    try {
      await onSave(invoice.id, notes);
      onClose();
    } catch (error) {
      console.error('Failed to save internal notes:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Ctrl+Enter or Cmd+Enter to save
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      handleSave();
    }
  };

  if (!invoice) return null;

  const title = translations.titleWithNumber.replace('{number}', invoice.invoiceNumber);
  const charCount = notes.length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: isMobile ? '100vh' : '300px'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="div">
          {title}
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
          disabled={saving}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Invoice info */}
          <Box sx={{ 
            p: 1.5, 
            bgcolor: 'background.default', 
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`
          }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Dodavatel: <strong>{invoice.supplierName}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Částka: <strong>{invoice.totalAmount.toLocaleString('cs-CZ')} {invoice.currency}</strong>
            </Typography>
          </Box>

          {/* Notes field */}
          <TextField
            multiline
            rows={isMobile ? 10 : 6}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={translations.placeholder}
            autoFocus
            fullWidth
            disabled={saving}
            onKeyDown={handleKeyDown}
            helperText={`${translations.characterCount.replace('{count}', charCount.toString())} • Tip: Ctrl+Enter pro uložení`}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: '0.95rem'
              }
            }}
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={saving}
          variant="outlined"
        >
          {translations.cancel}
        </Button>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          variant="contained"
          startIcon={<SaveIcon />}
          autoFocus
        >
          {saving ? 'Ukládání...' : translations.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InternalNotesDialog;

